import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListTodo } from 'lucide-react';
import ViewTabsBar from './ViewTabsBar';
import BulkSelectableTable from './BulkSelectableTable';
import KanbanBoard from './KanbanBoard';
import ListView from './ListView';
import GalleryView from './GalleryView';
import TimelineView from './TimelineView';
import ProjectCalendar from './ProjectCalendar';
import { readOnlyViewTabs, useViewTabs } from '../hooks/useViewTabs';

/**
 * One section (a "list" / "Tasks" block) inside a project overview.
 *
 * Each block owns its own view tabs so switching layout on one list doesn't
 * rewrite sibling lists in the same project. The view tabs catalog + prefs
 * are section-scoped via useViewTabs({ kind: 'section', sectionId }).
 *
 * Rendering dispatches by activeView.type; falls back to a list if the
 * view type is unknown or the catalog is still loading.
 *
 * Public/read-only callers can pass:
 *   • `readOnly`        - hides every edit affordance and loads no view tabs
 *                         (catalog/prefs); it picks the component, so hooks
 *                         are never called conditionally.
 *   • `seedViewType`    - picks the single view rendered in read-only mode
 *                         (e.g. 'board' for Roadmap, 'changelog' for
 *                         Changelog, 'gallery' for Ideas).
 *   • `showUpvotes`     - forwarded to GalleryView; turns the card into an
 *                         ideas-portal style upvotable card.
 *   • `onUpvote`,
 *     `onSignInRequired`,
 *     `isAuthenticated`  - forwarded to GalleryView.
 *   • `statusConfigs`    - forwarded to KanbanBoard so the board can render
 *                         columns without calling the auth-required statuses
 *                         API (the public team endpoint ships them inline).
 */
const SectionBlockView = ({
  viewTabs,
  section,
  tasks,
  projects,
  selectedProject,
  selectedProjectId,
  customFieldDefs,
  onTaskUpdate,
  onOptimisticUpdate,
  header,
  dropProps,
  extraClassName = '',
  readOnly = false,
  showUpvotes = false,
  onUpvote,
  onSignInRequired,
  isAuthenticated = true,
  statusConfigs = null,
}) => {
  const { t } = useTranslation();
  const { tabs, activeView, hiddenCatalog, isLocked, loading, actions } = viewTabs;
  const activeViewId = activeView?.id || null;
  const viewMode = activeView?.type || 'list';
  const visibleFields = activeView?.config?.visible_fields;

  const commonProps = {
    tasks,
    projects,
    onUpdate: onTaskUpdate,
    prefillProjectId: selectedProjectId,
    prefillSectionId: section.id,
    projectId: selectedProjectId,
    teamId: selectedProject?.team_id || null,
    readOnly,
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="text-[11px] text-muted-foreground py-2 px-2">{t('ui_views.section.loading_view')}</div>
      );
    }
    switch (viewMode) {
      case 'table':
        return (
          <BulkSelectableTable
            {...commonProps}
            onOptimisticUpdate={onOptimisticUpdate}
            showProjectColumn={false}
            showSearch
            showFilters
            showGrouping
            showAddButton
            showCompletedToggle
            compactMobile
            defaultGroupBy="none"
            defaultSortColumn="manual"
            tableId={`view-${activeViewId}-section-${section.id}`}
            readOnly={readOnly}
            onBulkUpdate={onTaskUpdate}
          />
        );
      case 'board':
        return (
          <KanbanBoard
            {...commonProps}
            statusConfigs={statusConfigs}
          />
        );
      case 'gallery':
        return (
          <GalleryView
            {...commonProps}
            visibleFields={visibleFields}
            customFieldDefs={customFieldDefs}
            showUpvotes={showUpvotes}
            onUpvote={onUpvote}
            onSignInRequired={onSignInRequired}
            isAuthenticated={isAuthenticated}
          />
        );
      case 'timeline':
        return (
          <TimelineView
            {...commonProps}
          />
        );
      case 'calendar':
        return (
          <ProjectCalendar
            {...commonProps}
          />
        );
      case 'list':
      default:
        return (
          <ListView
            {...commonProps}
            visibleFields={visibleFields}
            customFieldDefs={customFieldDefs}
          />
        );
    }
  };

  return (
    <div {...dropProps} className={`rounded-lg transition-colors ${extraClassName}`}>
      {header && (
        <div className="flex items-center gap-2 mb-1">
          <ListTodo className="w-3.5 h-3.5 text-blue-400" />
          {header}
          <span className="text-[11px] text-muted-foreground/50">{tasks.length}</span>
        </div>
      )}
      {/* Hide the tabs bar entirely in read-only single-view mode - there's
          nothing to switch between, and showing a lone tab adds chrome noise. */}
      {!readOnly && (
        <div className="mb-2">
          <ViewTabsBar
            tabs={tabs}
            activeView={activeView}
            hiddenCatalog={hiddenCatalog}
            isLocked={isLocked}
            actions={actions}
            loading={loading}
            customFieldDefs={customFieldDefs}
          />
        </div>
      )}
      {renderBody()}
    </div>
  );
};

const EditableSectionBlock = (props) => {
  const viewTabs = useViewTabs({ kind: 'section', sectionId: props.section.id });
  return <SectionBlockView {...props} viewTabs={viewTabs} />;
};

const SectionBlock = (props) => (props.readOnly
  ? <SectionBlockView {...props} viewTabs={readOnlyViewTabs(props.seedViewType)} />
  : <EditableSectionBlock {...props} />);

export default SectionBlock;
